/**
 * MLS-MAILAWAY-ENTITY-001 — Send Mail-Away built from the MLS entries.
 * create: multi-select the entities already captured in the MLS sections (fee / section / jurisdiction come
 *         from the entry, read-only), Method per entity, common Date Sent / Follow-up / Notes / Attachments →
 *         one independent request per selected entity (POST …/mail-away/batch).
 *         "Other Department (Manual)" is a fallback for a department not represented in the MLS entries.
 * resend: keeps department + section + fee of the previous cycle (POST …/{id}/resend).
 * Tracking-only: no email is dispatched.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { MlsMailAwayLetters, lettersValid } from './MlsMailAwayLetters';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, RotateCcw, Building2, AlertTriangle, Plus, Loader2, Check, Info } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';
import { MailAwayFilePicker, useMailAwayFiles } from '../mailaway/shared/MailAwayFilePicker';
import { MAIL_AWAY_ACTIVE } from '../mailaway/shared/mailAwayStatus';
import { MLS_MAILAWAY_SECTIONS, MLS_MAILAWAY_METHODS } from './rollupConfig';

const API = process.env.REACT_APP_BACKEND_URL;
const today = () => new Date().toISOString().slice(0, 10);

export const uploadMlsFiles = async (orderId, fileList, extra = {}) => {
  const out = [];
  for (const file of fileList) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('category', 'supporting_document');
    fd.append('component', 'mls');
    // MLS-DOC-CLASS-001: section / utility type / mail-away link travel with the upload
    Object.entries(extra).forEach(([k, v]) => { if (v) fd.append(k, v); });
    const res = await axios.post(`${API}/api/orders/${orderId}/files/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
    if (res.data?.file_id) out.push({ file_id: res.data.file_id, filename: file.name });
  }
  return out;
};

// MLS-MAILAWAY-PREMIUM-UI-003 — compact premium styling; error state only after touch/submit attempt
const inputCls = 'w-full h-10 px-3 text-sm border border-slate-200 rounded-xl bg-white shadow-sm transition-[border-color,box-shadow] duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 hover:border-slate-300 disabled:bg-slate-50 disabled:text-slate-400';
const inputErrCls = 'border-rose-300 focus:ring-rose-300/40 focus:border-rose-400';
const labelCls = 'block text-[13px] font-semibold text-slate-700 mb-1.5';
const Req = () => <span className="text-rose-500 ml-0.5">*</span>;
const SECTION_LABEL = Object.fromEntries(MLS_MAILAWAY_SECTIONS.map((s) => [s.value, s.label]));
const ENTITY_FIELD = { code_violations: 'office_holding_violations', open_permits: 'office_holding_permits', utility_accounts: 'water_sewer_collection_entity', trash: 'trash_collection_entity' };
const LEVEL_LABEL = { municipality: 'City', county: 'County', other: 'Other' };
const UTIL_LABEL = { water: 'Water', sewer: 'Sewer', water_sewer: 'Water/Sewer', trash: 'Trash', other: 'Utility' };
export const feeOf = (v) => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')); return Number.isFinite(n) ? n : null; };
export const fmtFee = (v) => { const n = feeOf(v); return n === null ? null : `$${n.toFixed(2)}`; };

/* Every MLS entry with an entity → one selectable candidate (deduped per section + name; PCL 2+ labelled). */
export const buildEntityCandidates = (order) => {
  const containers = [[1, order?.mls_search || {}], ...((order?.mls_parcels || []).map((p, i) => [i + 2, p || {}]))];
  const out = [];
  const seen = new Set();
  containers.forEach(([parcelIndex, c]) => {
    Object.entries(ENTITY_FIELD).forEach(([section, field]) => {
      ((c[section] || {}).entries || []).forEach((e, idx) => {
        const name = String(e?.[field] || '').trim();
        if (!name) return;
        const key = `${parcelIndex}|${section}|${name.toLowerCase()}`;
        if (seen.has(key)) return;
        seen.add(key);
        out.push({
          key, parcel_index: parcelIndex, section, section_label: SECTION_LABEL[section] || section, entry_index: idx,
          department_name: name, entry_uid: e.entry_uid || null, service_entity_id: e.service_entity_id || null,
          jurisdiction_level: e.service_level || null, jurisdiction_name: e.service_jurisdiction_name || null,
          utility_type: e.utility_type || null, fee: feeOf(e.fee_amount), fee_note: e.fee_note || null,
          address: e.address || null, phone: e.phone_number || null, fax: e.fax || null, email: e.email || null, website: e.website || null,
          service_jurisdiction_id: e.service_jurisdiction_id || null,
        });
      });
    });
  });
  return out;
};

// MLS-MAILAWAY-UI-VALIDATION-002: no per-entity Method — selection only (historical `method` values are untouched).
// MLS-MAILAWAY-PREMIUM-UI-003: compact row, animated check, subtle hover.
const CandidateRow = ({ c, checked, onToggle, alreadyActive }) => {
  const rid = c.key.replace(/[^a-z0-9]/gi, '-');
  const sub = [
    c.jurisdiction_level ? (LEVEL_LABEL[c.jurisdiction_level] || c.jurisdiction_level) : null,
    c.jurisdiction_name || null, c.address || null, c.email || null, c.fax ? `Fax ${c.fax}` : null,
  ].filter(Boolean);
  return (
    <label className={`group flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer select-none transition-[background-color,border-color,box-shadow,transform] duration-200 ${
        checked ? 'border-blue-300 bg-blue-50/70 shadow-[0_1px_0_rgba(37,99,235,0.08)]' : 'border-slate-200 bg-white hover:border-blue-200 hover:bg-slate-50/80 hover:shadow-sm'}`}
      data-testid={`mls-ma-entity-row-${rid}`} data-checked={checked}>
      <span className="relative flex-shrink-0 w-[18px] h-[18px]">
        <input type="checkbox" checked={checked} onChange={onToggle} className="peer sr-only" data-testid={`mls-ma-entity-check-${rid}`} />
        <span className={`absolute inset-0 rounded-md border-2 transition-[background-color,border-color,transform] duration-200 ease-out peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/50 peer-focus-visible:ring-offset-1 ${
            checked ? 'bg-blue-600 border-blue-600 scale-100' : 'bg-white border-slate-300 group-hover:border-blue-400'}`} />
        <Check className={`absolute inset-0 m-auto w-3 h-3 text-white transition-[opacity,transform] duration-200 ease-out ${checked ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} strokeWidth={3} />
      </span>
      <Building2 className="w-4 h-4 text-slate-400 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-slate-800 truncate" data-testid={`mls-ma-entity-name-${rid}`}>{c.department_name}</span>
          <span className="text-[11px] px-2 py-0.5 rounded-md border bg-indigo-50 text-indigo-700 border-indigo-200 font-medium">{c.utility_type ? UTIL_LABEL[c.utility_type] || c.section_label : c.section_label}</span>
          {c.parcel_index > 1 && <span className="text-[10px] px-1.5 py-0.5 rounded-md border bg-slate-100 text-slate-600 border-slate-200">PCL {c.parcel_index}</span>}
          {alreadyActive && <span className="text-[10px] px-1.5 py-0.5 rounded-md border bg-amber-50 text-amber-700 border-amber-200" data-testid={`mls-ma-entity-active-${rid}`}>request active</span>}
        </div>
        {sub.length > 0 && <div className="mt-0.5 text-[12px] text-slate-500 truncate">{sub.join(' · ')}</div>}
      </div>
      <div className="shrink-0">
        {c.fee !== null
          ? <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2 py-0.5" data-testid={`mls-ma-entity-fee-${rid}`}>{fmtFee(c.fee)}</span>
          : <span className="inline-flex items-center gap-1 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-1.5 py-0.5" data-testid={`mls-ma-entity-fee-missing-${rid}`} title="Set the fee on the MLS entry"><AlertTriangle className="w-3 h-3" />No fee on MLS entry</span>}
      </div>
    </label>
  );
};

/** mode: 'create' | 'resend' (resend keeps department + section + fee from `previous`). */
export const MlsMailAwaySendModal = ({ open, orderId, mode = 'create', previous = null, existingRequests = [], onDone, onClose }) => {
  const resend = mode === 'resend';
  const [order, setOrder] = useState(null);
  const [loadingOrder, setLoadingOrder] = useState(!resend);
  const [selected, setSelected] = useState({}); // key -> true (selected)
  const [manualOn, setManualOn] = useState(false);
  const [manual, setManual] = useState({ department_name: '', section: 'other', method: '', notes: '' });
  const [resendMethod, setResendMethod] = useState(previous?.method || '');
  const [dateSent, setDateSent] = useState(today());
  const [followUp, setFollowUp] = useState('');
  const [touched, setTouched] = useState({ dateSent: false, followUp: false }); // PREMIUM-UI-003: neutral until touched / submit attempt
  const [attempted, setAttempted] = useState(false);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [fileError, setFileError] = useState('');
  const [combine, setCombine] = useState(false); // LETTER-001
  const [combineEntity, setCombineEntity] = useState(null);
  const zone = useMailAwayFiles({ maxFiles: 3, maxTotalMb: 15, maxSizeMb: 5, onAccepted: () => setFileError('') });
  const files = Array.isArray(zone.file) ? zone.file : [];

  useEffect(() => {
    if (resend || !open) return;
    let alive = true;
    setLoadingOrder(true);
    axios.get(`${API}/api/orders/${orderId}/mls-search`).then((r) => { if (alive) setOrder(r.data); }).catch(() => toast.error('Could not load MLS entries')).finally(() => alive && setLoadingOrder(false));
    return () => { alive = false; };
  }, [orderId, open, resend]);

  const candidates = useMemo(() => buildEntityCandidates(order), [order]);
  const activeKeys = useMemo(() => new Set(existingRequests.filter((r) => MAIL_AWAY_ACTIVE.includes(r.status)).map((r) => `${r.section}|${String(r.department_name || '').toLowerCase()}`)), [existingRequests]);
  const chosen = candidates.filter((c) => c.key in selected);
  // MLS-MAILAWAY-ENTITY-VALIDATION-001: a manual row for a findings section needs that section's saved jurisdiction/entity
  const sectionsReady = order?.mls_mailaway_sections_ready || null;
  const manualSectionBlocked = manualOn && manual.section !== 'other' && !!sectionsReady && !sectionsReady[manual.section];
  const manualValid = !manualOn || (manual.department_name.trim() && manual.section && manual.method && !manualSectionBlocked && (manual.section !== 'other' || manual.notes.trim() || notes.trim()));
  const count = chosen.length + (manualOn ? 1 : 0);
  // MLS-MAILAWAY-UI-VALIDATION-002: Follow-up Date is mandatory (drives Overdue) and must not precede Date Sent
  const dateSentMissing = !dateSent;
  const followUpMissing = !followUp;
  const followUpBeforeSent = !!followUp && !!dateSent && followUp < dateSent;
  const datesValid = !dateSentMissing && !followUpMissing && !followUpBeforeSent;
  const showDateSentErr = (touched.dateSent || attempted) && dateSentMissing;
  const showFollowUpErr = (touched.followUp || attempted) && (followUpMissing || followUpBeforeSent);
  const lettersOk = resend || lettersValid(chosen, combine, combineEntity); // LETTER-001: every letter needs an addressable entity
  const valid = resend
    ? !!resendMethod && datesValid
    : count > 0 && manualValid && datesValid && lettersOk;
  const feeTotal = chosen.reduce((s, c) => s + (c.fee || 0), 0);

  const buildItems = () => chosen.map((c) => ({
    department_name: c.department_name, section: c.section, mlc_fee: c.fee, source: 'entry',
    entry_uid: c.entry_uid, service_entity_id: c.service_entity_id, parcel_index: c.parcel_index,
    jurisdiction_level: c.jurisdiction_level, jurisdiction_name: c.jurisdiction_name, utility_type: c.utility_type,
  }));

  const toggle = (c) => setSelected((prev) => { const n = { ...prev }; if (c.key in n) delete n[c.key]; else n[c.key] = true; return n; });

  const submit = async () => {
    if (busy || done) return; // duplicate-submit guard
    if (!valid) { setAttempted(true); return; }
    setBusy(true);
    try {
      const attachments = files.length ? await uploadMlsFiles(orderId, files) : [];
      if (resend) {
        await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/${previous.request_id}/resend`,
          { method: resendMethod, date_sent: dateSent || null, follow_up_date: followUp, notes: notes.trim() || null, attachments });
        toast.success(`Mail-Away resent — ${previous.department_name} (cycle ${(previous.cycle_index || 1) + 1})`);
      } else {
        const items = buildItems();
        if (manualOn) items.push({ department_name: manual.department_name.trim(), section: manual.section, method: manual.method, mlc_fee: null, source: 'manual', notes: manual.notes.trim() || null });
        const combine_letter = combine && combineEntity ? { entity: combineEntity } : null; // LETTER-001
        const res = await axios.post(`${API}/api/orders/${orderId}/components/mls/mail-away/batch`,
          { items, date_sent: dateSent || null, follow_up_date: followUp, notes: notes.trim() || null, attachments, combine_letter });
        const n = res.data?.count || items.length;
        const em = res.data?.email || {};
        if (em.status === 'sent') {
          toast.success(`${n} Mail-Away request${n === 1 ? '' : 's'} created — email sent to ${em.recipient}${em.attachment_count ? ` with ${em.attachment_count} attachment${em.attachment_count === 1 ? '' : 's'}` : ''}`);
        } else if (em.status === 'not_sent') {
          toast.info(`${n} Mail-Away request${n === 1 ? '' : 's'} created. Email to ${em.recipient} was NOT sent — ${em.not_sent_reason || 'live email delivery is not activated'}.`, { duration: 8000 });
        } else {
          toast.warning(`${n} Mail-Away request${n === 1 ? '' : 's'} saved, but the email to ${em.recipient || 'the client'} was not sent${em.error ? `: ${em.error}` : ''}. Use “Retry email” in Manage Mail-Away.`, { duration: 10000 });
        }
      }
      setDone(true);
      setTimeout(() => { onDone?.(); }, 450); // brief success state before the parent closes/refreshes
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Failed to create Mail-Away request'));
      setBusy(false);
    }
  };

  const dateErr = (show, msg, tid) => (show ? <div className="mt-1 text-[11px] text-rose-600 font-medium mls-ma-err-in" data-testid={tid}>{msg}</div> : null);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-[680px] p-0 gap-0 overflow-hidden rounded-2xl border border-slate-200/80 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.35)] max-h-[92vh] flex flex-col" data-testid="mls-ma-send-modal">
        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-slate-100">
          <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0">
            {resend ? <RotateCcw size={18} className="text-blue-600" /> : <Send size={18} className="text-blue-600" />}
          </div>
          <div className="min-w-0">
            <DialogTitle className="text-lg font-semibold tracking-tight text-slate-900">{resend ? 'Resend Mail-Away Request' : 'Send Mail-Away'}</DialogTitle>
            <DialogDescription className="sr-only">{resend ? 'Start a new cycle for the selected department' : 'Create Mail-Away requests for MLS entities'}</DialogDescription>
            {resend && <p className="text-xs text-slate-500 mt-0.5">Cycle {(previous?.cycle_index || 1) + 1} · {previous?.department_name}</p>}
          </div>
        </div>

        <div className="px-6 py-4 space-y-4 overflow-y-auto">
          {resend ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2.5 text-sm flex items-center gap-2 flex-wrap" data-testid="mls-ma-resend-target">
              <Building2 className="w-4 h-4 text-slate-400" />
              <span className="font-semibold text-slate-800">{previous?.department_name}</span>
              <span className="text-[11px] px-2 py-0.5 rounded-md border bg-indigo-50 text-indigo-700 border-indigo-200">{previous?.section_label || previous?.section}</span>
              {fmtFee(previous?.mlc_fee) && <span className="text-xs font-semibold text-emerald-700">{fmtFee(previous?.mlc_fee)}</span>}
            </div>
          ) : (
            <div className="space-y-2" data-testid="mls-ma-entity-list">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-slate-500">MLS entities on this order</span>
                {candidates.length > 0 && (
                  <button type="button" className="text-xs font-semibold text-blue-700 hover:text-blue-800 rounded-md px-1.5 py-0.5 transition-colors hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/50" data-testid="mls-ma-select-all-btn"
                    onClick={() => setSelected(chosen.length === candidates.length ? {} : Object.fromEntries(candidates.map((c) => [c.key, true])))}>
                    {chosen.length === candidates.length ? 'Clear all' : 'Select all'}
                  </button>
                )}
              </div>
              {loadingOrder ? (
                <div className="flex items-center gap-2 text-xs text-slate-500 py-3"><Loader2 className="w-3.5 h-3.5 animate-spin" />Loading MLS entries…</div>
              ) : candidates.length === 0 ? (
                <div className="text-xs text-slate-500 border border-dashed border-slate-300 rounded-xl px-3 py-3" data-testid="mls-ma-no-entities">
                  No entities saved in the MLS sections yet — add the department on the Code Violations, Permits or Utility entry, or use Other Department (Manual).
                </div>
              ) : (
                <div className="space-y-1.5 max-h-56 overflow-y-auto pr-0.5">
                  {candidates.map((c, i) => (
                    <div key={c.key} className="mls-ma-row-in" style={{ animationDelay: `${Math.min(i, 6) * 40}ms` }}>
                      <CandidateRow c={c} checked={c.key in selected} onToggle={() => toggle(c)}
                        alreadyActive={activeKeys.has(`${c.section}|${c.department_name.toLowerCase()}`)} />
                    </div>
                  ))}
                </div>
              )}
              <MlsMailAwayLetters orderId={orderId} chosen={chosen} combine={combine} setCombine={setCombine} combineEntity={combineEntity}
                setCombineEntity={setCombineEntity} dateSent={dateSent} buildItems={buildItems} />
              <div className={`rounded-xl border px-3 py-2.5 transition-colors duration-200 ${manualOn ? 'border-slate-300 bg-slate-50' : 'border-dashed border-slate-300 hover:border-slate-400'}`} data-testid="mls-ma-manual-block">
                <label className="flex items-center gap-3 text-sm font-medium text-slate-700 cursor-pointer">
                  <span className="relative w-[18px] h-[18px]">
                    <input type="checkbox" checked={manualOn} onChange={(e) => setManualOn(e.target.checked)} className="peer sr-only" data-testid="mls-ma-manual-toggle" />
                    <span className={`absolute inset-0 rounded-md border-2 transition-colors duration-200 peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500/50 ${manualOn ? 'bg-blue-600 border-blue-600' : 'bg-white border-slate-300'}`} />
                    <Check className={`absolute inset-0 m-auto w-3 h-3 text-white transition-[opacity,transform] duration-200 ${manualOn ? 'opacity-100 scale-100' : 'opacity-0 scale-50'}`} strokeWidth={3} />
                  </span>
                  <Plus className="w-4 h-4 text-slate-400" /> Other Department (Manual)
                </label>
                {manualOn && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 mls-ma-row-in">
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Department / Office Name<Req /></label>
                      <input type="text" value={manual.department_name} onChange={(e) => setManual({ ...manual, department_name: e.target.value })} className={inputCls} placeholder="e.g. Fire Marshal" data-testid="mls-ma-dept-input" />
                    </div>
                    <div>
                      <label className={labelCls}>Related MLS Section<Req /></label>
                      <select value={manual.section} onChange={(e) => setManual({ ...manual, section: e.target.value })} className={inputCls} data-testid="mls-ma-section-select">
                        {MLS_MAILAWAY_SECTIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className={labelCls}>Request Method<Req /></label>
                      <select value={manual.method} onChange={(e) => setManual({ ...manual, method: e.target.value })} className={inputCls} data-testid="mls-ma-method-select">
                        <option value="">Select…</option>
                        {MLS_MAILAWAY_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Note{manual.section === 'other' ? <Req /> : <span className="text-slate-400 font-normal"> (Optional)</span>}</label>
                      <input type="text" value={manual.notes} onChange={(e) => setManual({ ...manual, notes: e.target.value })} className={inputCls} placeholder="What was requested from this department?" data-testid="mls-ma-manual-notes-input" />
                    </div>
                    {manualSectionBlocked && (
                      <div className="sm:col-span-2 rounded-lg border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-800 flex items-center gap-1.5 mls-ma-err-in" data-testid="mls-ma-manual-entity-required">
                        <AlertTriangle className="w-3 h-3" />Select a jurisdiction/entity before initiating Mail-Away. ({MLS_MAILAWAY_SECTIONS.find((s) => s.value === manual.section)?.label || manual.section} has no saved entity.)
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
            {resend && (
              <div className="sm:col-span-2">
                <label className={labelCls}>Request Method<Req /></label>
                <select value={resendMethod} onChange={(e) => setResendMethod(e.target.value)} className={inputCls} data-testid="mls-ma-method-select">
                  <option value="">Select…</option>
                  {MLS_MAILAWAY_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>Date Sent<Req /></label>
              <input type="date" value={dateSent} required aria-required="true" aria-invalid={showDateSentErr || undefined}
                onChange={(e) => setDateSent(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, dateSent: true }))}
                className={`${inputCls} ${showDateSentErr ? inputErrCls : ''}`} data-testid="mls-ma-date-sent-input" />
              {dateErr(showDateSentErr, 'Date Sent is required.', 'mls-ma-date-sent-required')}
            </div>
            <div>
              <label className={`${labelCls} flex items-center gap-1.5`}>Follow-up Date<Req />
                <span className="inline-flex" title="Drives the Overdue indicator for this request"><Info className="w-3.5 h-3.5 text-slate-400" /></span>
              </label>
              <input type="date" value={followUp} min={dateSent || undefined} required aria-required="true" aria-invalid={showFollowUpErr || undefined}
                onChange={(e) => setFollowUp(e.target.value)} onBlur={() => setTouched((t) => ({ ...t, followUp: true }))}
                className={`${inputCls} ${showFollowUpErr ? inputErrCls : ''}`} data-testid="mls-ma-followup-input" />
              {dateErr(showFollowUpErr && followUpMissing, 'Follow-up Date is required.', 'mls-ma-followup-required')}
              {dateErr(showFollowUpErr && followUpBeforeSent, 'Follow-up Date cannot be earlier than Date Sent.', 'mls-ma-followup-before-sent')}
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Request Notes <span className="text-slate-400 font-normal">(Optional)</span></label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                className="w-full px-3 py-2 text-sm border border-slate-200 rounded-xl bg-white shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 resize-y"
                placeholder="Add a note for this request…" data-testid="mls-ma-notes-input" />
            </div>
            <div className="sm:col-span-2">
              <label className={labelCls}>Attachments <span className="text-slate-400 font-normal">(Optional)</span></label>
              <MailAwayFilePicker zone={zone} error={fileError} onClearError={() => setFileError('')} accent="blue" testIdPrefix="mls-ma-request"
                maxFiles={3} maxTotalMb={15} maxSizeMb={5} compact helperText="PDF, DOC, DOCX, JPG, PNG • Max 5 MB per file • Up to 3 files • 15 MB total" />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center gap-3 px-6 py-4 border-t border-slate-100 bg-white">
          <Button type="button" variant="outline" onClick={onClose} disabled={busy}
            className="h-11 px-6 rounded-xl border-slate-200 text-slate-700 hover:bg-slate-50 focus-visible:ring-2 focus-visible:ring-blue-500/40 transition-colors" data-testid="mls-ma-cancel-form-btn">Cancel</Button>
          <Button type="button" onClick={submit} disabled={busy || done || !valid} aria-disabled={!valid || undefined}
            className={`flex-1 h-11 rounded-xl text-[15px] font-semibold shadow-sm transition-[background-color,box-shadow,transform] duration-200 focus-visible:ring-2 focus-visible:ring-blue-500/50 focus-visible:ring-offset-2 ${
              done ? 'bg-emerald-600 hover:bg-emerald-600' : 'bg-blue-600 hover:bg-blue-700 hover:shadow-md active:scale-[0.99] disabled:bg-blue-600/40 disabled:shadow-none'}`}
            data-testid="mls-ma-submit-btn">
            {done ? <><Check size={16} className="mr-2" strokeWidth={3} />{resend ? 'Resent' : 'Created'}</>
              : busy ? <><Loader2 size={16} className="mr-2 animate-spin" />{resend ? 'Resending…' : 'Creating…'}</>
                : resend ? <><RotateCcw size={16} className="mr-2" />Resend Request</>
                  : <><Send size={16} className="mr-2" />Create {count > 1 ? `${count} ` : ''}Mail-Away Request{count === 1 ? '' : 's'}{feeTotal > 0 ? ` · ${fmtFee(feeTotal)}` : ''}</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MlsMailAwaySendModal;
